import React, { useState, useEffect } from 'react';
import { Settings, Plus, Trash2, Check, X } from 'lucide-react';
import { Card, Button, Alert } from '../components/UI';
import { configAPI } from '../api/apiClient';
import toast from 'react-hot-toast';

/**
 * Configuration Panel Page - Complete Config Management UI
 */
const ConfigPanelPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Basic Config
  const [configKey, setConfigKey] = useState('');
  const [latestVersion, setLatestVersion] = useState('');
  const [forceUpdate, setForceUpdate] = useState(false);
  const [apkUrl, setApkUrl] = useState('');

  // URLs
  const [apiUrl, setApiUrl] = useState('');
  const [cdnUrl, setCdnUrl] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [supportUrl, setSupportUrl] = useState('');
  const [privacyUrl, setPrivacyUrl] = useState('');
  const [termsUrl, setTermsUrl] = useState('');

  // Maintenance
  const [maintenance, setMaintenance] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  // Features
  const [features, setFeatures] = useState({});

  // API URLs List
  const [apiUrls, setApiUrls] = useState([]);
  const [newApiUrl, setNewApiUrl] = useState('');
  const [newApiPriority, setNewApiPriority] = useState(1);

  // Proxy Fallback
  const [proxyEnabled, setProxyEnabled] = useState(false);
  const [proxyUrls, setProxyUrls] = useState([]);
  const [newProxyUrl, setNewProxyUrl] = useState('');

  // Rate Limiting
  const [rateLimitEnabled, setRateLimitEnabled] = useState(false);
  const [rateLimitWindow, setRateLimitWindow] = useState(900000);
  const [rateLimitMax, setRateLimitMax] = useState(100);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await configAPI.get();
      const configData = response.data.data;

      // Set basic config
      setConfigKey(configData.configKey || '');
      setLatestVersion(configData.latestVersion || '');
      setForceUpdate(configData.forceUpdate || false);
      setApkUrl(configData.apkUrl || '');

      // Set URLs
      setApiUrl(configData.apiUrl || '');
      setCdnUrl(configData.cdnUrl || '');
      setSupportEmail(configData.supportEmail || '');
      setSupportUrl(configData.supportUrl || '');
      setPrivacyUrl(configData.privacyUrl || '');
      setTermsUrl(configData.termsUrl || '');

      // Set maintenance
      setMaintenance(configData.maintenance || false);
      setMaintenanceMessage(configData.maintenanceMessage || '');

      // Set features
      setFeatures(configData.features || {});

      // Set API URLs
      setApiUrls(configData.apiUrls || []);

      // Set proxy fallback
      const proxy = configData.proxyFallback || {};
      setProxyEnabled(proxy.enabled || false);
      setProxyUrls(proxy.proxies || []);

      // Set rate limiting
      const rateLimit = configData.rateLimiting || {};
      setRateLimitEnabled(rateLimit.enabled || false);
      setRateLimitWindow(rateLimit.windowMs || 900000);
      setRateLimitMax(rateLimit.maxRequests || 100);

      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const markChanges = () => setHasChanges(true);

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      const updateData = {
        configKey,
        latestVersion,
        forceUpdate,
        apkUrl,
        apiUrl,
        cdnUrl,
        supportEmail,
        supportUrl,
        privacyUrl,
        termsUrl,
        maintenance,
        maintenanceMessage,
        features,
        apiUrls,
        proxyFallback: {
          enabled: proxyEnabled,
          proxies: proxyUrls,
        },
        rateLimiting: {
          enabled: rateLimitEnabled,
          windowMs: rateLimitWindow,
          maxRequests: rateLimitMax,
        },
      };

      await configAPI.update(updateData);

      toast.success('Configuration saved successfully');
      setHasChanges(false);
      loadConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const addApiUrl = () => {
    if (!newApiUrl.trim()) {
      toast.error('Please enter an API URL');
      return;
    }
    const newUrl = {
      _id: { $oid: Date.now().toString() },
      url: newApiUrl,
      priority: newApiPriority,
      isActive: false,
    };
    setApiUrls([...apiUrls, newUrl]);
    setNewApiUrl('');
    setNewApiPriority(1);
    markChanges();
  };

  const removeApiUrl = (index) => {
    setApiUrls(apiUrls.filter((_, i) => i !== index));
    markChanges();
  };

  const toggleApiUrlActive = (index) => {
    const updated = apiUrls.map((item, i) =>
      i === index ? { ...item, isActive: !item.isActive } : item
    );
    setApiUrls(updated);
    markChanges();
  };

  const addProxyUrl = () => {
    if (!newProxyUrl.trim()) {
      toast.error('Please enter a proxy URL');
      return;
    }
    setProxyUrls([...proxyUrls, newProxyUrl]);
    setNewProxyUrl('');
    markChanges();
  };

  const removeProxyUrl = (index) => {
    setProxyUrls(proxyUrls.filter((_, i) => i !== index));
    markChanges();
  };

  const toggleFeature = (featureName) => {
    setFeatures({
      ...features,
      [featureName]: !features[featureName],
    });
    markChanges();
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin">
          <Settings className="text-purple-400" size={32} />
        </div>
        <p className="text-gray-400 mt-2">Loading configuration...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
            <Settings size={32} className="text-purple-400" />
            <span>Configuration Management</span>
          </h1>
          <p className="text-gray-400 mt-2">
            Manage all app configuration settings and feature flags
          </p>
        </div>
        {hasChanges && (
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              onClick={loadConfig}
              disabled={saving}
            >
              <X size={18} /> Discard
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveConfig}
              disabled={saving}
            >
              <Check size={18} /> {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      {/* Basic Configuration */}
      <Card>
        <h2 className="text-xl font-bold text-white mb-4">Basic Configuration</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Config Key
            </label>
            <input
              type="text"
              value={configKey}
              onChange={(e) => {
                setConfigKey(e.target.value);
                markChanges();
              }}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="e.g., DEFAULT"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Latest Version
            </label>
            <input
              type="text"
              value={latestVersion}
              onChange={(e) => {
                setLatestVersion(e.target.value);
                markChanges();
              }}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="e.g., 2.0.0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              APK URL
            </label>
            <input
              type="text"
              value={apkUrl}
              onChange={(e) => {
                setApkUrl(e.target.value);
                markChanges();
              }}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="https://example.com/app.apk"
            />
          </div>

          <div className="flex items-center space-x-4">
            <label className="text-gray-300">Force Update</label>
            <button
              onClick={() => {
                setForceUpdate(!forceUpdate);
                markChanges();
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                forceUpdate ? 'bg-green-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  forceUpdate ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </Card>

      {/* URLs Management */}
      <Card>
        <h2 className="text-xl font-bold text-white mb-4">URLs Management</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Primary API URL
            </label>
            <input
              type="text"
              value={apiUrl}
              onChange={(e) => {
                setApiUrl(e.target.value);
                markChanges();
              }}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="https://api.example.com/api"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              CDN URL
            </label>
            <input
              type="text"
              value={cdnUrl}
              onChange={(e) => {
                setCdnUrl(e.target.value);
                markChanges();
              }}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="https://cdn.example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Support Email
            </label>
            <input
              type="email"
              value={supportEmail}
              onChange={(e) => {
                setSupportEmail(e.target.value);
                markChanges();
              }}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="support@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Support URL
            </label>
            <input
              type="text"
              value={supportUrl}
              onChange={(e) => {
                setSupportUrl(e.target.value);
                markChanges();
              }}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="https://support.example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Privacy Policy URL
            </label>
            <input
              type="text"
              value={privacyUrl}
              onChange={(e) => {
                setPrivacyUrl(e.target.value);
                markChanges();
              }}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="https://example.com/privacy"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Terms of Service URL
            </label>
            <input
              type="text"
              value={termsUrl}
              onChange={(e) => {
                setTermsUrl(e.target.value);
                markChanges();
              }}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="https://example.com/terms"
            />
          </div>
        </div>
      </Card>

      {/* API URLs List */}
      <Card>
        <h2 className="text-xl font-bold text-white mb-4">Backup API URLs</h2>
        <div className="space-y-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={newApiUrl}
              onChange={(e) => setNewApiUrl(e.target.value)}
              placeholder="Enter API URL"
              className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <input
              type="number"
              value={newApiPriority}
              onChange={(e) => setNewApiPriority(parseInt(e.target.value))}
              placeholder="Priority"
              min="1"
              className="w-24 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <Button variant="primary" onClick={addApiUrl}>
              <Plus size={18} /> Add
            </Button>
          </div>

          {apiUrls.length > 0 && (
            <div className="space-y-2">
              {apiUrls.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                  <div className="flex-1">
                    <p className="text-white font-medium break-all">{item.url}</p>
                    <p className="text-gray-400 text-sm">Priority: {item.priority}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleApiUrlActive(index)}
                      className={`px-3 py-1 rounded text-sm font-medium transition ${
                        item.isActive
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      {item.isActive ? 'Active' : 'Inactive'}
                    </button>
                    <button
                      onClick={() => removeApiUrl(index)}
                      className="p-2 bg-red-600 hover:bg-red-700 rounded transition text-white"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Feature Flags */}
      <Card>
        <h2 className="text-xl font-bold text-white mb-4">Feature Flags</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(features).map(([key, value]) => (
            <div
              key={key}
              className="flex items-center justify-between p-4 bg-gray-700 rounded-lg"
            >
              <div>
                <p className="text-white font-medium capitalize">
                  {key.replace(/([A-Z])/g, ' $1')}
                </p>
                <p className="text-gray-400 text-xs">
                  {key === 'ads' && 'Display advertisements'}
                  {key === 'newUi' && 'New UI design'}
                  {key === 'offlineMode' && 'Offline playback'}
                  {key === 'chromecast' && 'Chromecast support'}
                  {key === 'downloads' && 'Content downloads'}
                  {key === 'recommendations' && 'Recommendations'}
                  {key === 'parentalControl' && 'Parental controls'}
                </p>
              </div>
              <button
                onClick={() => {
                  toggleFeature(key);
                  markChanges();
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  value ? 'bg-green-600' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    value ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </Card>

      {/* Maintenance Mode */}
      <Card>
        <h2 className="text-xl font-bold text-white mb-4">Maintenance Mode</h2>
        {maintenance && (
          <Alert type="warning" message="⚠️ Server is currently in maintenance mode" />
        )}
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
            <span className="text-gray-300 font-medium">Enable Maintenance Mode</span>
            <button
              onClick={() => {
                setMaintenance(!maintenance);
                markChanges();
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                maintenance ? 'bg-red-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  maintenance ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {maintenance && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Maintenance Message
              </label>
              <textarea
                value={maintenanceMessage}
                onChange={(e) => {
                  setMaintenanceMessage(e.target.value);
                  markChanges();
                }}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Server under maintenance. Please try again later."
                rows="3"
              />
            </div>
          )}
        </div>
      </Card>

      {/* Proxy Fallback */}
      <Card>
        <h2 className="text-xl font-bold text-white mb-4">Proxy Fallback</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
            <span className="text-gray-300 font-medium">Enable Proxy Fallback</span>
            <button
              onClick={() => {
                setProxyEnabled(!proxyEnabled);
                markChanges();
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                proxyEnabled ? 'bg-green-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  proxyEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {proxyEnabled && (
            <div>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newProxyUrl}
                  onChange={(e) => setNewProxyUrl(e.target.value)}
                  placeholder="Enter proxy URL"
                  className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <Button variant="primary" onClick={addProxyUrl}>
                  <Plus size={18} /> Add
                </Button>
              </div>

              {proxyUrls.length > 0 && (
                <div className="space-y-2">
                  {proxyUrls.map((url, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
                    >
                      <p className="text-white break-all">{url}</p>
                      <button
                        onClick={() => removeProxyUrl(index)}
                        className="p-2 bg-red-600 hover:bg-red-700 rounded transition text-white"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Rate Limiting */}
      <Card>
        <h2 className="text-xl font-bold text-white mb-4">Rate Limiting</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
            <span className="text-gray-300 font-medium">Enable Rate Limiting</span>
            <button
              onClick={() => {
                setRateLimitEnabled(!rateLimitEnabled);
                markChanges();
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                rateLimitEnabled ? 'bg-green-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  rateLimitEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {rateLimitEnabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Window (milliseconds)
                </label>
                <input
                  type="number"
                  value={rateLimitWindow}
                  onChange={(e) => {
                    setRateLimitWindow(parseInt(e.target.value));
                    markChanges();
                  }}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-gray-400 text-xs mt-1">Default: 900000 (15 minutes)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Max Requests per Window
                </label>
                <input
                  type="number"
                  value={rateLimitMax}
                  onChange={(e) => {
                    setRateLimitMax(parseInt(e.target.value));
                    markChanges();
                  }}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-gray-400 text-xs mt-1">Default: 100 requests</p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Save Footer */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 p-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <p className="text-yellow-400 font-medium">You have unsaved changes</p>
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                onClick={loadConfig}
                disabled={saving}
              >
                <X size={18} /> Discard
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveConfig}
                disabled={saving}
              >
                <Check size={18} /> {saving ? 'Saving...' : 'Save All Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigPanelPage;
